/**
 * This mixin is used for the Wux-Simulation created for Dental. Not to be confused with the original simulations mixin used for previous simulations.
 */
import WuxSimulationSorting from './wux-simulation-sorting';

export default {
  mixins: [WuxSimulationSorting],
  data() {
    return {
      loadingData: false,
    };
  },
  computed: {
    canAccessSimulation() {
      return this.$access(this.$store, 'category', this.simId);
    },
    categoryState() {
      return this.$store.getters['categoryStates/categoryStates'][this.sectionId];
    },
    categoryStates() {
      return this.$store.getters['categoryStates/categoryStates'];
    },
    categoryStatesFiltered() {
      return this.sections
        .map( section => {
          return section?.id && this.categoryStates[section.id];
        })
        .filter(s => s);
    },
    categoryStatesOrdered() {
      return this.sections.map( section => {
        return this.categoryStates[section.id] || {
          categoryId: section.id,
        };
      });
    },
    completedStatus() {
      return this.$store.getters['categoryStatuses/completedStatus'];
    },
    firstSection () {
      return this.sections[0];
    },
    flashcards() {
      if (this.isMultiSectionSimulation) return this.flashcardsBySection(this.sectionId);
      return this.flashcardsBySection(this.simId);
    },
    flashcardListStatuses() {
      return this.flashcardStatesOrdered.map(state => {
        return this.flashcardReviewStatus(state);
      });
    },
    flashcardStates() {
      return this.$store.getters['flashcardStates/flashcardStates'];
    },
    flashcardStateList() {
      return this.$store.getters['flashcardStates/flashcardStateList'];
    },
    flashcardStatesOrdered() {
      return this.flashcards.map( flashcard => {
        return this.flashcardStates[`${flashcard.id}-category-${this.section.id}`] || { flashcardId: flashcard.id };
      });
    },
    hasMultipleSections() {
      return this.simulation?.children?.length > 1;
    },
    inProgressStatus() {
      return this.$store.getters['categoryStatuses/inProgressStatus'];
    },
    isCompleted() {
      return this.categoryState?.categoryStatusId === this.completedStatus.id;
    },
    isDentalSimulation() {
      return this.simulationCategoryType?.simulationType === 'DentalSimulation';
    },
    isInProgress() {
      return this.categoryState?.categoryStatusId === this.inProgressStatus.id;
    },
    isMultiSectionSimulation() {
      return this.isSimulation && this.hasMultipleSections;
    },
    isNotStarted() {
      return this.categoryState?.categoryStatusId === this.notStartedStatus.id;
    },
    isSimulation() {
      if (!this.simId || !this.simulation) return false;
      const ids = this.$store.getters['categoryTypes/testSimulationCategoryTypes'].map(t => t.id);
      return ids.includes(this.simulation.categoryTypeId);
    },
    isSimulationComplete() {
      return this.categoryStates[this.lastSection.id]?.categoryStatusId === this.completedStatus.id;
    },
    lastSection () {
      return this.sections[this.sections.length - 1];
    },
    nextSection() {
      return this.sections.find(section => section.id === this.nextSectionId);
    },
    nextSectionId() {
      const currentSectionIndex = this.sections.findIndex(section => section.id === this.section.id);

      if (currentSectionIndex === this.sections.length - 1) {
        return null;
      }

      return this.sections[currentSectionIndex + 1].id;
    },
    notStartedStatus() {
      return this.$store.getters['categoryStatuses/notStartedStatus'];
    },
    section() {
      return this.$store.getters['categories/categories'][this.sectionId];
    },
    sectionId() {
      if (this.isMultiSectionSimulation) return +this.$route?.params['sectionId'];
      return this.simId;
    },
    sectionIndex() {
      if (!this.sectionId) return -1;
      return this.sections.findIndex( section => section.id === this.sectionId);
    },
    sections() {
      if (!this.isMultiSectionSimulation) return [this.simulation];
      let categories = this.$store.getters['categories/all'];
      const childrenIds = this.simulation?.children || [];
      return  categories.
        filter( c => childrenIds.includes(c.id) ).
        sort( (a, b) => a.number - b.number );
    },
    sectionTallies() {
      return this.sections.map((section) => {
        const answeredCorrectly = this.flashcardStateList.filter(
          (flashcardState) => flashcardState.categoryId === section.id && flashcardState.correct,
        );
        return {
          sectionName: section.name,
          sectionId: section.id,
          correct: answeredCorrectly.length,
          incorrect: this.flashcardsBySection(section.id).length - answeredCorrectly.length,
        };
      });
    },
    sectionTally() {
      if (!this.sectionId) return null;
      return this.sectionTallies.find( tally => tally.sectionId === this.sectionId);
    },
    simConfig() {
      const configs = this.$store.getters['simulationConfigs/all'];
      return configs.find( c => c.categoryId == this.simId);
    },
    simId() {
      return this.$route?.params['simId'];
    },
    simulation() {
      return this.$store.getters['categories/categories'][this.simId];
    },
    simulationCategoryType() {
      if (!this.simulation) return null;
      return this.$store.getters['categoryTypes/testSimulationCategoryTypes'].find(t => t.id === this.simulation.categoryTypeId);
    },
  },
  methods: {
    flashcardReviewStatus(flashcardState) {
      const { flashcardId, answerIds, rawAnswerData } = flashcardState;
      const bookmark = this.$store.getters['bookmarks/get'][`Flashcard-${flashcardId}`];

      let sectionIndex = this.sections.findIndex(section => section.id === flashcardState.categoryId);

      if (sectionIndex === -1) {
        const flashcard = this.$store.getters['flashcards/flashcards'][flashcardId];
        if (flashcard && flashcard.categoryIds) {
          sectionIndex = this.sections.findIndex(section =>
            flashcard.categoryIds.includes(section.id),
          );
        }
      }

      const sectionNumber = sectionIndex !== -1 ? sectionIndex + 1 : null;
      return {
        id: flashcardId,
        isAnswered: answerIds?.length > 0 || rawAnswerData && Object.values(rawAnswerData).length > 0,
        isBookmarked: bookmark && !bookmark.deleted,
        isCorrect: !!flashcardState.correct,
        sectionId: flashcardState.categoryId,
        sectionName: sectionNumber ? `Section ${sectionNumber}` : null,
      };
    },
    setCategoryState(category, status) {
      if (!category?.id) return console.error('category is required');

      const currentCategoryState = this.categoryStatesFiltered.find(cs => cs.categoryId === category.id);

      // if the current category state has the same status, do nothing
      if (currentCategoryState?.categoryStatusId === status.id) return;

      // if the current category state is 'completed' do nothing
      if (currentCategoryState?.categoryStatusId === this.completedStatus.id) return;

      // if the current category state is 'in progress' and the new status is 'not started', do nothing
      if (currentCategoryState?.categoryStatusId === this.inProgressStatus.id && status.id === this.notStartedStatus.id) return;

      const params = {
        number: 0,
        categoryStatusId: status.id,
        categoryStatusName: status.name,
        categoryId: category.id,
        mobileCreatedAt: Date.now(),
      };
      this.$analytics.events.simulationCategoryState(params);
      if (!this.isMultiSectionSimulation && status.id === this.completedStatus.id) {
        this.submitSimulation();
      }
      if (!this.isMultiSectionSimulation && status.id === this.inProgressStatus.id) {
        this.startSimulation(this.simulation.name, this.simId);
      } else if (
        this.isMultiSectionSimulation &&
        this.firstSection.id === category.id &&
        status.id === this.inProgressStatus.id
      ) {
        this.startSimulation(this.simulation.name, this.simId);
      }
      return this.$store.dispatch('categoryStates/sendCategoryState', params);
    },
    submitSection() {
      if (this.isSimulationComplete) return;
      this.setCategoryState(this.section, this.completedStatus);
      if (this.nextSectionId) {
        this.$router.push({
          name: 'simSection#break',
          params: {
            simId: this.simId,
            sectionId: this.sectionId,
          },
        });
      } else if (this.isMultiSectionSimulation) {
        // Check if it's a Dental simulation
        if (this.isDentalSimulation) {
          this.submitSimulation();
        } else {
          // Non-Dental multi-section sims go to outro
          this.$router.push({
            name: 'sims#outro',
            params: {
              simId: this.simId,
            },
          });
        }
      }
    },
    submitSimulation() {
      // Only set simulation state for multi-section simulations
      // Single-section simulations already have state set by the initial setCategoryState call
      if (this.isMultiSectionSimulation) {
        this.setCategoryState(this.simulation, this.completedStatus);
      }
      this.$analytics.events.simulationFinish({
        simulationId: this.simId,
        simulationName: this.simulation.name,
      });
      this.$router.push({
        name: 'sims#performance',
        params: {
          simId: this.simId,
        },
      });
    },
    startSimulation(simName, simId) {
      this.$analytics.events.simulationStart({
        simulationId: simId,
        simulationName: simName,
      });
    },
    flashcardStateKey(flashcardId, sectionId) {
      return `${flashcardId}-category-${sectionId}`;
    },
    formattedTime(seconds) {
      if (seconds === null) return '';
      if (seconds < 0) {
        return '0:00';
      }
      // in hours, minutes and seconds:
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor(seconds % 3600 / 60);
      const secondsLeft = Math.floor(seconds % 3600 % 60);
      return `${hours ? `${hours}:` : ''}${minutes < 10 ? `0${minutes}` : minutes}:${secondsLeft < 10 ? `0${secondsLeft}` : secondsLeft}`;
    },
    getFlashcardState(flashcardId, sectionId) {
      return this.flashcardStates[this.flashcardStateKey(flashcardId, sectionId)] || {};
    },
    timeLeft(startTime, minutes) {
      const expiration = startTime + minutes * 60 * 1000;
      return expiration - Date.now();
    },
    timerTick(status, timeInMinutes) {
      if (this.loadingData) return;
      if (!status) {
        this.timeLeftInSeconds = -1;
        return;
      }
      // When the time limit has not been defined on the category
      if (!timeInMinutes) {
        this.timeLeftInSeconds = 0;
        return;
      }

      this.timeLeftInSeconds = Math.round(
        this.timeLeft(this.categoryState?.mobileCreatedAt, timeInMinutes) / 1000,
      );
    },
  },

};
